import { z } from "zod";

export const themePresetIds = ["neutral", "monkeytilt"] as const;
export const themeShellIds = ["neutral", "monkeytilt-v2"] as const;
export const themeFontIds = ["system", "monkeytilt"] as const;
export const productIds = ["casino", "sports", "rips", "racing"] as const;
export const themeAssetKeys = [
  "monkeytilt-mark",
  "monkeytilt-wordmark",
  "tilt-racing-logo",
  "monkeytilt-sidebar",
  "monkeytilt-favicon",
] as const;

export const semanticColorKeys = [
  "page",
  "surface",
  "surfaceRaised",
  "border",
  "text",
  "textMuted",
  "primary",
  "primaryStrong",
  "primaryHover",
  "onPrimary",
] as const;

const hexColorSchema = z.string().regex(/^#[0-9a-f]{6}$/i);

function safeHref(value: string | null) {
  if (value === null) return true;
  if (
    value.length === 0 ||
    value.length > 2048 ||
    [...value].some((character) => character.charCodeAt(0) < 32)
  ) {
    return false;
  }
  if (
    value.startsWith("/") &&
    !value.startsWith("//") &&
    !value.includes("\\")
  ) {
    return true;
  }
  try {
    const parsed = new URL(value);
    return (
      parsed.protocol === "https:" &&
      Boolean(parsed.host) &&
      !parsed.username &&
      !parsed.password &&
      !parsed.hash
    );
  } catch {
    return false;
  }
}

const brandSchema = z
  .object({
    displayName: z.string().min(1).max(64),
    shortName: z.string().min(1).max(16),
    markAssetKey: z.literal("monkeytilt-mark").nullable(),
    wordmarkAssetKey: z
      .enum(["monkeytilt-wordmark", "tilt-racing-logo"])
      .nullable(),
    logoAlt: z.string().min(1).max(96),
  })
  .strict();

const colorsSchema = z
  .object({
    page: hexColorSchema.optional(),
    surface: hexColorSchema.optional(),
    surfaceRaised: hexColorSchema.optional(),
    border: hexColorSchema.optional(),
    text: hexColorSchema.optional(),
    textMuted: hexColorSchema.optional(),
    primary: hexColorSchema.optional(),
    primaryStrong: hexColorSchema.optional(),
    primaryHover: hexColorSchema.optional(),
    onPrimary: hexColorSchema.optional(),
  })
  .strict();

const assetsSchema = z
  .object({
    sidebarBackgroundKey: z.literal("monkeytilt-sidebar").nullable(),
    faviconKey: z.literal("monkeytilt-favicon").nullable(),
  })
  .strict();

const productSchema = z
  .object({
    id: z.enum(productIds),
    label: z.string().min(1).max(32),
    shortLabel: z.string().min(1).max(16),
    iconId: z.enum(productIds),
    href: z.string().nullable().refine(safeHref, "Unsafe product URL"),
    enabled: z.boolean(),
  })
  .strict()
  .superRefine((product, context) => {
    if (product.iconId !== product.id) {
      context.addIssue({
        code: "custom",
        message: "Product icon must match its product id",
        path: ["iconId"],
      });
    }
    if (product.enabled && product.href === null) {
      context.addIssue({
        code: "custom",
        message: "Enabled products require an href",
        path: ["href"],
      });
    }
  });

export const themeColorDefaults = {
  neutral: {
    page: "#09121b",
    surface: "#101e2c",
    surfaceRaised: "#1a2d40",
    border: "#22384d",
    text: "#f1f5f8",
    textMuted: "#8495a5",
    primary: "#1ea7f2",
    primaryStrong: "#1689c8",
    primaryHover: "#39b7ff",
    onPrimary: "#06111a",
  },
  monkeytilt: {
    page: "#09090b",
    surface: "#18181b",
    surfaceRaised: "#27272a",
    border: "#3f3f46",
    text: "#fafafa",
    textMuted: "#a1a1aa",
    primary: "#ffe500",
    primaryStrong: "#f4dc03",
    primaryHover: "#eab308",
    onPrimary: "#09090b",
  },
} as const;

function relativeLuminance(color: string) {
  const channels = [1, 3, 5].map(
    (index) => Number.parseInt(color.slice(index, index + 2), 16) / 255,
  );
  const linear = channels.map((channel) =>
    channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4,
  );
  return 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
}

function contrastRatio(first: string, second: string) {
  const [low, high] = [
    relativeLuminance(first),
    relativeLuminance(second),
  ].sort((a, b) => a - b);
  return (high + 0.05) / (low + 0.05);
}

const themeInputSchema = z
  .object({
    schemaVersion: z.literal(1),
    preset: z.enum(themePresetIds),
    shellVariant: z.enum(themeShellIds),
    fontPreset: z.enum(themeFontIds),
    brand: brandSchema,
    colors: colorsSchema,
    assets: assetsSchema,
    products: z.array(productSchema).length(4),
    heroId: z.null(),
  })
  .strict()
  .superRefine((theme, context) => {
    const expectedShell =
      theme.preset === "monkeytilt" ? "monkeytilt-v2" : "neutral";
    const expectedFont =
      theme.preset === "monkeytilt" ? "monkeytilt" : "system";
    if (
      theme.shellVariant !== expectedShell ||
      theme.fontPreset !== expectedFont
    ) {
      context.addIssue({
        code: "custom",
        message:
          "Preset, shellVariant, and fontPreset must select one coherent registry entry",
      });
    }

    const ids = theme.products.map(({ id }) => id);
    if (
      new Set(ids).size !== productIds.length ||
      productIds.some((id) => !ids.includes(id))
    ) {
      context.addIssue({
        code: "custom",
        message: "Products must contain each registered product exactly once",
        path: ["products"],
      });
    }

    const colors = { ...themeColorDefaults[theme.preset], ...theme.colors };
    if (contrastRatio(colors.text, colors.page) < 4.5) {
      context.addIssue({
        code: "custom",
        message: "text/page contrast must be at least 4.5:1",
        path: ["colors"],
      });
    }
    if (contrastRatio(colors.onPrimary, colors.primary) < 4.5) {
      context.addIssue({
        code: "custom",
        message: "onPrimary/primary contrast must be at least 4.5:1",
        path: ["colors"],
      });
    }
  });

export const themeV1Schema = themeInputSchema.transform((theme) => ({
  ...theme,
  colors: { ...themeColorDefaults[theme.preset], ...theme.colors },
}));

export type ThemeV1 = z.output<typeof themeV1Schema>;

export const publicSettingsSchema = z
  .object({
    tenant: z
      .object({
        slug: z.string(),
        name: z.string(),
        theme: themeV1Schema,
      })
      .strict(),
    world: z
      .object({
        slug: z.string().nullable(),
        raceIntervalS: z.number().nullable(),
        streamCount: z.number().nullable(),
        fieldSize: z.number().nullable(),
        rtpBp: z.number().nullable(),
        seasonLengthDays: z.number().nullable(),
      })
      .strict(),
  })
  .strict();

export type PublicSettings = z.output<typeof publicSettingsSchema>;
